import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, RefreshCw, Share2, Download, Copy, Check, ExternalLink } from "lucide-react";
import { SiteFooter } from "@/components/site/FinalCTA";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { PAGES } from "@/constants/testIds";
import { toast } from "sonner";

/**
 * Parse the LLM output into a list of blocks:
 *   { type: "epigraph" | "heading" | "paragraph", text }
 * The first non-heading, non-empty paragraph becomes the epigraph.
 */
function parseBlocks(text) {
  if (!text) return [];
  const blocks = [];
  let seenEpigraph = false;
  for (const raw of text.split(/\n{2,}/)) {
    const t = raw.trim();
    if (!t) continue;
    if (t.startsWith("## ")) {
      blocks.push({ type: "heading", text: t.replace(/^##\s+/, "") });
      continue;
    }
    if (t.startsWith("# ")) {
      blocks.push({ type: "heading", text: t.replace(/^#\s+/, "") });
      continue;
    }
    if (!seenEpigraph) {
      blocks.push({ type: "epigraph", text: t.replace(/^["“”']|["“”']$/g, "") });
      seenEpigraph = true;
    } else {
      blocks.push({ type: "paragraph", text: t });
    }
  }
  return blocks;
}

function ImagePlate({ img, i }) {
  const [broken, setBroken] = useState(false);
  if (!img || broken) return null;
  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="my-12"
    >
      <div className={`overflow-hidden rounded-2xl border border-ivory-300 bg-ivory ${i === 0 ? "spotlight" : ""}`}>
        <img
          src={img.url}
          alt={img.alt || ""}
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          className={`w-full ${i === 0 ? "h-[480px]" : "h-[380px]"} object-cover grayscale-[0.08] sepia-[0.06]`}
        />
      </div>
      <figcaption className="mt-3 flex flex-wrap items-baseline justify-between gap-3 text-xs text-ink-50">
        <span className="italic">
          Photo by{" "}
          {img.photographer_url ? (
            <a href={`${img.photographer_url}?utm_source=indianfamilytree&utm_medium=referral`}
               target="_blank" rel="noopener noreferrer"
               className="text-ink hover:text-saffron transition ink-link">
              {img.credit_name}
            </a>
          ) : (
            <span className="text-ink">{img.credit_name}</span>
          )}{" "}on{" "}
          <a href="https://unsplash.com?utm_source=indianfamilytree&utm_medium=referral"
             target="_blank" rel="noopener noreferrer"
             className="text-ink hover:text-saffron transition ink-link">Unsplash</a>
        </span>
        {img.photo_url && (
          <a href={`${img.photo_url}?utm_source=indianfamilytree&utm_medium=referral`}
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-ink-50 hover:text-ink transition">
            View original <ExternalLink size={11} />
          </a>
        )}
      </figcaption>
    </motion.figure>
  );
}

export default function HeritageTopic() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [article, setArticle] = useState({ sections: [] });
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const cardUrl = `${API_BASE}/heritage/topic/${slug}/card`;
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/indian-heritage/${slug}`
    : "";

  const [pending, setPending] = useState(null); // {label, category, section_id, elapsed}

  async function load(refresh = false) {
    setError(null);
    if (refresh) setRefreshing(true); else setBusy(true);
    try {
      const { data, status } = await api.get(
        `/heritage/topic/${slug}${refresh ? "?refresh=true" : ""}`,
        { validateStatus: (s) => (s >= 200 && s < 300) || s === 202 }
      );
      if (status === 202) {
        setPending({
          label: data?.label || slug.replace(/-/g, " "),
          category: data?.category || "",
          startedAt: Date.now(),
        });
        setData(null);
        // begin polling
        pollUntilReady();
      } else {
        setPending(null);
        setData(data);
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false); setRefreshing(false);
    }
  }

  async function pollUntilReady() {
    const startedAt = Date.now();
    while (true) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data, status } = await api.get(`/heritage/topic/${slug}`, {
          validateStatus: (s) => (s >= 200 && s < 300) || s === 202,
        });
        if (status === 200 && data?.content) {
          setData(data);
          setPending(null);
          return;
        }
        // still generating — update elapsed
        setPending((p) => (p ? { ...p, elapsed: Math.round((Date.now() - startedAt) / 1000) } : p));
        // Safety: stop after 3 minutes
        if (Date.now() - startedAt > 3 * 60 * 1000) {
          setError("Generation is taking longer than expected. Please try again.");
          setPending(null);
          return;
        }
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
        setPending(null);
        return;
      }
    }
  }

  useEffect(() => {
    load(false);
    api.get("/heritage/article").then((r) => setArticle(r.data || { sections: [] })).catch(() => {});
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const blocks = useMemo(() => parseBlocks(data?.content), [data]);
  const images = data?.images || [];

  // Interleave images between prose sections.
  // Cover image goes right after the epigraph; the remaining images
  // are distributed evenly between subsequent headings.
  const rendered = useMemo(() => {
    const out = [];
    let imgIdx = 0;
    const headingCount = blocks.filter((b) => b.type === "heading").length;
    // Positions where an image should go (index of heading, 0-based)
    const insertAfterHeadingIndexes = headingCount > 1 && images.length > 1
      ? [0, Math.floor(headingCount / 2), headingCount - 1].filter((v, i, a) => a.indexOf(v) === i)
      : [];
    let seenHeadings = 0;
    for (let i = 0; i < blocks.length; i++) {
      out.push(blocks[i]);
      if (blocks[i].type === "epigraph" && images[0]) {
        out.push({ type: "image", img: images[imgIdx++], idx: imgIdx - 1 });
      }
      if (blocks[i].type === "heading") {
        seenHeadings += 1;
      }
      // Insert next image after two paragraphs following a chosen heading
      // For simplicity: after each of the chosen heading indexes, once we've
      // shown that heading + 2 paragraphs, drop the image.
    }
    // Second pass: fill remaining images between headings evenly
    if (imgIdx < images.length) {
      const withImages = [];
      const remaining = images.slice(imgIdx);
      const headings = out.map((b, i) => ({ ...b, i })).filter((b) => b.type === "heading");
      const slots = [];
      if (headings.length > 1 && remaining.length > 0) {
        const step = Math.max(1, Math.floor(headings.length / (remaining.length + 1)));
        for (let k = step; k < headings.length && slots.length < remaining.length; k += step) {
          slots.push(headings[k].i);
        }
      }
      for (let i = 0; i < out.length; i++) {
        withImages.push(out[i]);
        // Insert an image AFTER the second paragraph following a slot heading
        const idxInSlots = slots.indexOf(i);
        if (idxInSlots >= 0 && remaining[idxInSlots]) {
          // walk forward 2 paragraphs and inject the image
          let placed = false, paras = 0;
          for (let j = withImages.length; j < out.length; j++) {
            if (out[j].type === "paragraph") { paras += 1; }
            if (paras >= 2) {
              // append everything up to j, then image, then continue
              for (let k = i + 1; k <= j; k++) withImages.push(out[k]);
              withImages.push({ type: "image", img: remaining[idxInSlots], idx: imgIdx });
              imgIdx += 1;
              i = j; placed = true; break;
            }
          }
          if (!placed) {
            withImages.push({ type: "image", img: remaining[idxInSlots], idx: imgIdx });
            imgIdx += 1;
          }
        }
      }
      return withImages;
    }
    return out;
  }, [blocks, images]);

  // Prev/next within section
  const nav = useMemo(() => {
    for (const s of article.sections || []) {
      const idx = (s.items || []).findIndex((it) => it.slug === slug);
      if (idx >= 0) {
        const prev = idx > 0 ? s.items[idx - 1] : null;
        const next = idx < s.items.length - 1 ? s.items[idx + 1] : null;
        return { section: s, prev, next };
      }
    }
    return { section: null, prev: null, next: null };
  }, [article, slug]);

  return (
    <div data-testid={PAGES.heritageTopic} className="min-h-screen bg-ivory text-ink">
      {/* Hero */}
      <section className="relative pt-40 pb-16 border-b border-ivory-300">
        <img
          aria-hidden
          src="https://images.unsplash.com/photo-1705837863332-7162639852d8?crop=entropy&cs=srgb&fm=jpg&w=1600&q=60"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.12] mix-blend-multiply pointer-events-none"
          alt=""
        />
        <div className="relative max-w-[900px] mx-auto px-6 md:px-10">
          <button
            onClick={() => navigate("/indian-heritage")}
            className="inline-flex items-center gap-2 text-sm text-ink-50 hover:text-ink ink-link mb-8"
          >
            <ArrowLeft size={14} /> Back to Indian Heritage
          </button>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-saffron">
            {data?.category && <span>{data.category}</span>}
            {nav.section && <span className="text-ink-50">· {nav.section.title}</span>}
          </div>
          <h1 className="mt-4 font-serif tracking-tighter leading-[0.95] text-ink text-[3rem] sm:text-[4rem] md:text-[5rem]">
            {data?.label || slug.replace(/-/g, " ")}
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShareOpen((v) => !v)}
              data-testid="heritage-topic-share"
              className="inline-flex items-center gap-2 rounded-full bg-tulsi text-ivory px-5 py-2.5 text-sm font-medium hover:bg-tulsi-deep transition"
            >
              <Share2 size={14} /> Share as card
            </button>
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              data-testid={PAGES.heritageTopicRefresh}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ink-50 hover:text-ink transition disabled:opacity-50"
              title="Regenerate this note"
            >
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Regenerating…" : "Regenerate"}
            </button>
            {data?.model && (
              <span className="text-[10px] uppercase tracking-[0.28em] text-ink-50">
                Written with {data.model.replace("anthropic/", "")}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Share card panel */}
      {shareOpen && (
        <section className="relative border-b border-ivory-300 bg-ivory-100/60 py-12">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 md:col-span-7">
              <div className="relative rounded-2xl border border-ivory-300 overflow-hidden shadow-[0_20px_80px_-30px_rgba(10,15,13,0.35)] bg-ivory">
                <img
                  src={cardUrl}
                  alt="Shareable heritage card"
                  data-testid="heritage-topic-share-image"
                  className="w-full h-auto block"
                />
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <span className="text-xs uppercase tracking-[0.3em] text-saffron">Share as a card</span>
              <h3 className="mt-3 font-serif text-3xl tracking-tight text-ink leading-tight">
                A keepsake for WhatsApp, Instagram &amp; more.
              </h3>
              <p className="mt-3 text-ink-50 leading-relaxed">
                Save the image or share the link — every card carries the epigraph, the topic and
                the IndianFamilyTree tricolour footer.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={cardUrl}
                  download={`${slug}-heritage.png`}
                  data-testid="heritage-topic-download"
                  className="inline-flex items-center gap-2 rounded-full bg-saffron text-ivory px-5 py-2.5 text-sm font-medium hover:bg-saffron-deep transition"
                >
                  <Download size={14} /> Download image
                </a>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      if (navigator.share) {
                        await navigator.share({ title: data?.label || "Indian Heritage", text: `“${(blocks.find(b => b.type === 'epigraph')?.text || '')}” — IndianFamilyTree.com`, url: shareUrl });
                      } else {
                        await navigator.clipboard.writeText(shareUrl);
                        setCopied(true); setTimeout(() => setCopied(false), 1500);
                        toast.success("Link copied");
                      }
                    } catch { /* user cancelled */ }
                  }}
                  data-testid="heritage-topic-copy"
                  className="inline-flex items-center gap-2 rounded-full border border-ink/25 text-ink px-5 py-2.5 text-sm hover:border-ink hover:bg-ink hover:text-ivory transition"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? "Copied" : "Copy link"}
                </button>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${data?.label || "Indian Heritage"} — “${(blocks.find(b => b.type === 'epigraph')?.text || '')}”`)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-ink-50 hover:text-ink ink-link"
                >
                  Post on X →
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${data?.label || "Indian Heritage"} · IndianFamilyTree.com ${shareUrl}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-ink-50 hover:text-ink ink-link"
                >
                  Send on WhatsApp →
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          {busy && !pending && (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-4 rounded bg-ivory-200 animate-pulse" style={{ width: `${60 + (i % 3) * 15}%` }} />
              ))}
            </div>
          )}

          {pending && (
            <div className="relative border border-ivory-300 rounded-2xl p-8 md:p-10 bg-ivory-100/60">
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-saffron">
                <span className="inline-block w-2 h-2 rounded-full bg-saffron animate-pulse" />
                Composing a new heritage note
              </div>
              <h3 className="mt-4 font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
                Claude is writing about {pending.label}…
              </h3>
              <p className="mt-4 text-ink-50 leading-relaxed max-w-xl">
                We're fetching a fresh 900-word editorial and four Unsplash photographs. This usually
                takes about thirty seconds. You can stay here — the page will update the moment the
                archive is ready.
              </p>
              <div className="mt-6 flex items-center gap-3 text-sm text-ink-50">
                <div className="relative w-40 h-1 rounded-full bg-ivory-200 overflow-hidden">
                  <motion.span
                    className="absolute inset-y-0 left-0 bg-saffron"
                    initial={{ width: "10%" }}
                    animate={{ width: ["10%", "90%", "10%"] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  />
                </div>
                <span>Elapsed · {pending.elapsed || 0}s</span>
              </div>
            </div>
          )}

          {error && (
            <div className="border border-rust/40 bg-rust/5 text-rust p-6 rounded-xl">
              <div className="text-sm">Couldn't load this topic — {error}</div>
              <button onClick={() => load(true)} className="mt-3 text-xs underline">Try again</button>
            </div>
          )}

          {!busy && !pending && !error && data && (
            <motion.article
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid={PAGES.heritageTopicContent}
              className="space-y-5"
            >
              {rendered.map((b, i) => {
                if (b.type === "image") return <ImagePlate key={`img-${i}`} img={b.img} i={b.idx} />;
                if (b.type === "epigraph") {
                  return (
                    <p key={i} className="font-serif italic text-2xl md:text-3xl text-tulsi leading-relaxed border-l-2 border-saffron pl-6">
                      {b.text}
                    </p>
                  );
                }
                if (b.type === "heading") {
                  return (
                    <h3 key={i} className="mt-14 mb-2 font-serif text-3xl md:text-4xl tracking-tight text-ink">
                      {b.text}
                    </h3>
                  );
                }
                return (
                  <p key={i} className="text-lg md:text-xl text-ink font-light leading-relaxed">
                    {b.text}
                  </p>
                );
              })}
            </motion.article>
          )}

          {/* Attribution footer */}
          {!busy && images.length > 0 && (
            <div className="mt-16 pt-6 border-t border-ivory-300 text-xs text-ink-50 leading-relaxed">
              Photographs sourced via the{" "}
              <a href="https://unsplash.com?utm_source=indianfamilytree&utm_medium=referral"
                 target="_blank" rel="noopener noreferrer"
                 className="text-ink hover:text-saffron transition ink-link">Unsplash</a>{" "}
              API and used under the Unsplash License. Individual credits appear below each image.
              Prose composed by claude-sonnet-5 for IndianFamilyTree.com — a SocialPulse heritage
              project.
            </div>
          )}
        </div>
      </section>

      {/* Prev / next within section */}
      {nav.section && (
        <section className="relative pb-24 border-t border-ivory-300">
          <div className="max-w-[900px] mx-auto px-6 md:px-10 grid grid-cols-2 gap-6 pt-10">
            <div>
              {nav.prev && (
                <Link
                  to={`/indian-heritage/${nav.prev.slug}`}
                  className="group block border border-ivory-300 rounded-2xl p-6 hover:border-ink transition"
                >
                  <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">← Previous</div>
                  <div className="mt-2 font-serif text-2xl text-ink group-hover:text-saffron transition leading-tight">
                    {nav.prev.label}
                  </div>
                </Link>
              )}
            </div>
            <div className="text-right">
              {nav.next && (
                <Link
                  to={`/indian-heritage/${nav.next.slug}`}
                  className="group block border border-ivory-300 rounded-2xl p-6 hover:border-ink transition"
                >
                  <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Next →</div>
                  <div className="mt-2 font-serif text-2xl text-ink group-hover:text-saffron transition leading-tight">
                    {nav.next.label}
                  </div>
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  );
}
